import React, { createContext, useReducer } from 'react';

import { ICustomFilters } from 'modules/_common/models';
import {
  TBasicInformationTableState,
  TCalculationTableState,
  TGuestSelectionTableState,
  TPaymentSelectionTableState,
} from 'modules/accommodations/features/reservations/models';

type TStepperState = {
  intermediateStepCheck: boolean;
  isIntermediateStepModalVisible: boolean;
  isFirstStepCompleted: boolean;
  firstStepLocalstorage: object;
  isSecondStepCompleted: boolean;
  secondStepLocalstorage: object;
  isThirdStepCompleted: boolean;
  isFourthStepCompleted: boolean;
  fourthStepLoadingState: boolean;
  renderIndex: number;
  submitForm: boolean;
  checkPopupModalRedirect: boolean;
};

type TStepState = {
  currentStep: string;
};
const INITIAL_STEPPER_STATE: TStepperState = {
  intermediateStepCheck: true,
  isIntermediateStepModalVisible: false,
  isFirstStepCompleted: false,
  firstStepLocalstorage: {},
  isSecondStepCompleted: false,
  secondStepLocalstorage: {},
  isThirdStepCompleted: false,
  isFourthStepCompleted: false,
  fourthStepLoadingState: false,
  renderIndex: 0,
  submitForm: false,
  checkPopupModalRedirect: true,
};

const INITIAL_STEP_STATE: TStepState = {
  currentStep: '',
};

const INITIAL_STATE_FIRST_STEP: TBasicInformationTableState = {
  isModalVisible: false,
  tableData: [],
  formValues: {
    term: '',
    partner: '',
    calculationStatus: '',
    date: [],
    serviceId: '',
    languageId: '',
    currencyId: '',
    adults: 0,
    teens: 0,
    children: 0,
    babies: 0,
  },
  isStepOneFormDirty: false,
};

const INITIAL_STATE_SECOND_STEP: TGuestSelectionTableState = {
  newGuestPhysicalPersonFormData: {},
  // existing guest
  isModalVisible: false,
  tableData: [],
};

const INITIAL_STATE_THIRD_STEP: TCalculationTableState = {
  isServiceSideBarOpen: false,
  isDiscountSideBarOpen: false,
  isServiceEdit: false,
  isDiscountEdit: false,
  servicesTableData: [],
  discountsTableData: [],
  rowIndex: null,
};

export const INITIAL_STATE_FOURTH_STEP: TPaymentSelectionTableState = {
  isChecked: false,
  isSubmitDisabled: true,
  isApplyPayment: true,
  isDeletePayment: false,
  paymentPayload: {
    instalmentAmount: 0,
    isPercentage: false,
    paymentModelId: '',
    calculationTotalAmount: 0,
  },
};

//state for ReservationsTable.tsx component, filtering table
type TReservationListTableFilterState = {
  sortField: string;
  sortOrder: string;

  isFilterModalVisible: boolean;
  filtersState: ICustomFilters[];
  isDeleteFilterModalVisible: boolean;
  currentFilterId: string | null;
  isOrdinaryUser: boolean;
  isDefaultFilterModalVisible: boolean;
  assignedUsersIds: string[] | undefined;
  isFilterVisible: boolean;
  disableIsActiveTab: boolean;
  isCustomFilterClicked: boolean;

  //ReservationsFilter.tsx component - reservations list filter
  isSomeReservationStatusNull: boolean;
};
export const INITIAL_STATE_RESERVATION_LIST_TABLE_FILTER_STATE: TReservationListTableFilterState = {
  sortField: '',
  sortOrder: '',

  isFilterModalVisible: false,
  filtersState: [],
  isDeleteFilterModalVisible: false,
  currentFilterId: null,
  isOrdinaryUser: false,
  isDefaultFilterModalVisible: false,
  assignedUsersIds: undefined,
  isFilterVisible: false,
  disableIsActiveTab: false,
  isCustomFilterClicked: false,

  //ReservationsFilter.tsx component - reservations list filter
  isSomeReservationStatusNull: false,
};

interface IReservationNewContext {
  stepperState: TStepperState;
  currentStep: TStepState;
  firstStepTableState: TBasicInformationTableState;
  secondStepTableState: TGuestSelectionTableState;
  thirdStepTableState: TCalculationTableState;
  fourthStepTableState: TPaymentSelectionTableState;
  reservationListTableFilterState: TReservationListTableFilterState;
  setStepperState: (step: Partial<TStepperState>) => void;
  setCurrentStep: (step: Partial<TStepState>) => void;
  setFirstStepTableState: (newState: Partial<TBasicInformationTableState>) => void;
  setSecondStepTableState: (newState: Partial<TGuestSelectionTableState>) => void;
  setThirdStepTableState: (newState: Partial<TCalculationTableState>) => void;
  setFourthStepTableState: (newState: Partial<TPaymentSelectionTableState>) => void;
  setReservationListTableFilterState: (newState: Partial<TReservationListTableFilterState>) => void;
}

export const ReservationNewContext = createContext<IReservationNewContext>(
  {} as IReservationNewContext,
);

export const ReservationNewContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [stepperState, setStepperState] = useReducer(
    (state: TStepperState, newState: Partial<TStepperState>): TStepperState => ({
      ...state,
      ...newState,
    }),
    INITIAL_STEPPER_STATE,
  );

  // set current step page
  const [currentStep, setCurrentStep] = useReducer(
    (state: TStepState, newState: Partial<TStepState>): TStepState => ({
      ...state,
      ...newState,
    }),
    INITIAL_STEP_STATE,
  );

  const [firstStepTableState, setFirstStepTableState] = useReducer(
    (
      state: TBasicInformationTableState,
      stateUpdate: Partial<TBasicInformationTableState>,
    ): TBasicInformationTableState => ({
      ...state,
      ...stateUpdate,
    }),
    INITIAL_STATE_FIRST_STEP,
  );

  const [secondStepTableState, setSecondStepTableState] = useReducer(
    (
      state: TGuestSelectionTableState,
      stateUpdate: Partial<TGuestSelectionTableState>,
    ): TGuestSelectionTableState => ({
      ...state,
      ...stateUpdate,
    }),
    INITIAL_STATE_SECOND_STEP,
  );

  const [thirdStepTableState, setThirdStepTableState] = useReducer(
    (
      state: TCalculationTableState,
      stateUpdate: Partial<TCalculationTableState>,
    ): TCalculationTableState => ({
      ...state,
      ...stateUpdate,
    }),
    INITIAL_STATE_THIRD_STEP,
  );

  const [fourthStepTableState, setFourthStepTableState] = useReducer(
    (
      state: TPaymentSelectionTableState,
      stateUpdate: Partial<TPaymentSelectionTableState>,
    ): TPaymentSelectionTableState => ({
      ...state,
      ...stateUpdate,
    }),
    INITIAL_STATE_FOURTH_STEP,
  );

  //state for ReservationList.tsx component, filtering table
  const [reservationListTableFilterState, setReservationListTableFilterState] = useReducer(
    (
      state: TReservationListTableFilterState,
      stateUpdate: Partial<TReservationListTableFilterState>,
    ): TReservationListTableFilterState => ({
      ...state,
      ...stateUpdate,
    }),
    INITIAL_STATE_RESERVATION_LIST_TABLE_FILTER_STATE,
  );

  const value = {
    stepperState,
    currentStep,
    firstStepTableState,
    secondStepTableState,
    thirdStepTableState,
    fourthStepTableState,
    reservationListTableFilterState,
    setStepperState,
    setCurrentStep,
    setFirstStepTableState,
    setSecondStepTableState,
    setThirdStepTableState,
    setFourthStepTableState,
    setReservationListTableFilterState,
  };

  return <ReservationNewContext.Provider value={value}>{children}</ReservationNewContext.Provider>;
};
